import React, { useState } from 'react';
import './UTLayoutConfigModal.css';

// ── Layout config map ──────────────────────────────────────────────
// Keys: lowercased "component||measurement"
// Types:
//   fixed-rows          – pre-defined row labels, no count input
//   scanline-checkboxes – fixed option list, user ticks which to include
//   roman               – count → (i)(ii)(iii)…
//   roman-center        – count → (i)(ii)…Center (n)
//   numeral             – count → 1 2 3…
//   nozzle              – count → N1 N2…, plus Neck/Plate checkboxes
//   rafter              – count → roman-center, plus per-type position checkboxes
//   supporting-column   – two counts: columns + locations/column → roman-top labels
//   shell-course        – count + radio (3/4/5/Variable); per-course roman labels
//   pipe-segment        – pipes count + per-pipe location count; numeral labels

const LAYOUT_CONFIGS = {
  // ── Floor ──────────────────────────────────────────────────────
  'floor (external)||outside edge of bottom': {
    type: 'scanline-checkboxes',
    rowHeader: 'Location',
    checkboxLabel: 'Locations per scanline',
    options: ['Shell Course 1 (i)', 'Annular thickness (ii)', 'Annular projection (iii)', 'A-Height (iv)'],
  },
  'floor (internal)||annular plates': {
    type: 'numeral',
    rowHeader: 'Plate number',
    rowCountLabel: 'Number of plates',
    defaultRowCount: null,
    specialType: 'annular-plates',
  },
  'floor (internal)||flat bottom': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'floor (internal)||floor sump': {
    type: 'fixed-rows',
    rowHeader: 'Location',
    rows: ['Sump'],
  },
  'floor (internal)||membrane plates': {
    type: 'numeral',
    rowHeader: 'Plate number',
    rowCountLabel: 'Number of plates',
    defaultRowCount: null,
    // Same dedicated builder as Annular plates — nominal moved to
    // row 2 with merge-left sentinels + auto MIN/MAX/AVG/degradation
    // formulas.
    specialType: 'annular-plates',
  },

  // ── Heads ──────────────────────────────────────────────────────
  // All four head layouts reuse the flat-bottom builder: transposed
  // (locations as column headers, scanlines down col A), merge-left
  // nominal row, auto MIN/MAX/AVG/degradation formulas.
  'heads (external)||front head': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'heads (external)||rear head': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'heads (internal)||front head': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'heads (internal)||rear head': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },

  // ── Nozzles ────────────────────────────────────────────────────
  'nozzles (external)||roof nozzles': {
    type: 'nozzle',
    rowHeader: 'Nozzle',
    rowCountLabel: 'Number of nozzles',
    defaultRowCount: null,
  },
  'nozzles (external)||shell nozzles': {
    type: 'nozzle',
    rowHeader: 'Nozzle',
    rowCountLabel: 'Number of nozzles',
    defaultRowCount: null,
  },
  'nozzles (internal)||roof nozzles': {
    type: 'nozzle',
    rowHeader: 'Nozzle',
    rowCountLabel: 'Number of nozzles',
    defaultRowCount: null,
  },
  'nozzles (internal)||shell nozzles': {
    type: 'nozzle',
    rowHeader: 'Nozzle',
    rowCountLabel: 'Number of nozzles',
    defaultRowCount: null,
  },

  // ── Pipes ──────────────────────────────────────────────────────
  'pipes (external)||piping systems': {
    type: 'pipe-segment',
    pipeCountLabel: 'Number of pipes',
  },
  'pipes (internal)||bubbling pipes': {
    type: 'pipe-segment',
    pipeCountLabel: 'Number of pipes',
  },
  'pipes (internal)||heating pipes': {
    type: 'pipe-segment',
    pipeCountLabel: 'Number of pipes',
  },
  'pipes (internal)||piping systems': {
    type: 'pipe-segment',
    pipeCountLabel: 'Number of pipes',
  },

  // ── Roof ───────────────────────────────────────────────────────
  // Fixed-roof layouts (external + internal) reuse the flat-bottom
  // builder — transposed, merge-left nominal, auto summary formulas.
  'roof (external)||fixed roof': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'roof (internal)||fixed roof': {
    type: 'roman-center',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },

  // ── Rafters ────────────────────────────────────────────────────
  'rafters||l rafters (double)': {
    type: 'rafter',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    positions: ['Top flange-R (A)', 'Top flange-L (B)', 'Web-R (C)', 'Web-L (D)'],
  },
  'rafters||ipe rafters': {
    type: 'rafter',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    positions: ['Top flange-R (A)', 'Top flange-L (B)', 'Web (C)', 'Bottom flange-R (D)', 'Bottom flange-L (E)'],
  },
  'rafters||t rafters': {
    type: 'rafter',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    positions: ['Top flange-R (A)', 'Top flange-L (B)', 'Web (C)'],
  },
  'rafters||upn rafters': {
    type: 'rafter',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    positions: ['Top flange (A)', 'Web (B)', 'Bottom flange (C)'],
  },
  'rafters||l rafters': {
    type: 'rafter',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    positions: ['Top flange (A)', 'Web (B)'],
  },
  'rafters||upn rafters (double)': {
    type: 'rafter',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    positions: ['Top flange-R (A)', 'Top flange-L (B)', 'Web-R (C)', 'Web-L (D)', 'Bottom flange-R (E)', 'Bottom flange-L (F)'],
  },

  // ── Shell ──────────────────────────────────────────────────────
  // Shell layouts reuse the flat-bottom builder (transposed +
  // merge-left nominal + auto summary formulas). Labels stay
  // plain roman ((i), (ii), (iii), …) — no "Center" suffix — because
  // the type is `roman`, not `roman-center`.
  'shell (external)||shell': {
    type: 'roman',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'shell (internal)||shell': {
    type: 'roman',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'flat-bottom',
  },
  'shell (external)||shell courses': {
    type: 'shell-course',
    courseCountLabel: 'Number of shell courses',
  },
  'shell (internal)||shell courses': {
    type: 'shell-course',
    courseCountLabel: 'Number of shell courses',
  },

  // ── Corner-weld (CW-OSI single fixed sub-card) ─────────────────
  // Same configuration shape as `roman` (number of locations textbox +
  // include-nominal toggle) but renders a minimal flat measurement grid
  // with no summary rows and no table splitting.
  'floor (internal)||bottom corner weld': {
    type: 'roman',
    rowHeader: 'Location',
    rowCountLabel: 'Number of locations',
    defaultRowCount: null,
    specialType: 'corner-weld',
  },

  // ── Roof (Internal) ────────────────────────────────────────────
  // Supporting-columns now matches the pipe-segment shape: user
  // picks # of columns, then per-column location-count textboxes
  // appear. Numeric labels (1, 2, …) with the last replaced by
  // "Top (n)". Each column renders as its own split sub-table with
  // summary rows (handled by buildPipeSegmentTemplate with
  // groupHeader: 'Column').
  'roof (internal)||supporting columns': {
    type: 'supporting-column',
    columnCountLabel: 'Number of columns',
    defaultColumnCount: null,
  },
};

function layoutKey(component, measurement) {
  return `${(component || '').trim().toLowerCase()}||${(measurement || '').trim().toLowerCase()}`;
}

export function getLayoutConfig(component, measurement) {
  return LAYOUT_CONFIGS[layoutKey(component, measurement)] || null;
}

// ── Label helpers ──────────────────────────────────────────────────

function toRomanParens(n) {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['m', 'cm', 'd', 'cd', 'c', 'xc', 'l', 'xl', 'x', 'ix', 'v', 'iv', 'i'];
  let r = '';
  let x = n;
  for (let i = 0; i < vals.length; i++) {
    while (x >= vals[i]) { r += syms[i]; x -= vals[i]; }
  }
  return `(${r})`;
}

// Returns roman-center labels: (i)(ii)…Center (n)
function makeRomanCenterLabels(count) {
  return Array.from({ length: count }, (_, i) => {
    const roman = toRomanParens(i + 1);
    return i === count - 1 ? `Center ${roman}` : roman;
  });
}

// Returns shell-course location labels for a given measurement-count choice.
//   '3' → 30 (i), ⅓ h (ii), ⅔ h (iii)
//   '4' → 30 (i), ¼ h (ii), ½ h (iii), ¾ h (iv)
//   '5' → 30 (i), ⅕ h (ii), ⅖ h (iii), ⅗ h (iv), ⅘ h (v)
//   '10cm' → 30 (i), 100 (ii), 200 (iii), …, 100·(N-1) (Nth)  [centimetre marks]
//   'other' → plain roman numerals (catch-all for >5 measurements)
function makeShellCourseLocations(count, choice) {
  if (choice === '3') {
    return [`30 ${toRomanParens(1)}`, `⅓ h ${toRomanParens(2)}`, `⅔ h ${toRomanParens(3)}`];
  }
  if (choice === '4') {
    return [
      `30 ${toRomanParens(1)}`, `¼ h ${toRomanParens(2)}`,
      `½ h ${toRomanParens(3)}`, `¾ h ${toRomanParens(4)}`,
    ];
  }
  if (choice === '5') {
    return [
      `30 ${toRomanParens(1)}`, `⅕ h ${toRomanParens(2)}`, `⅖ h ${toRomanParens(3)}`,
      `⅗ h ${toRomanParens(4)}`, `⅘ h ${toRomanParens(5)}`,
    ];
  }
  if (choice === '10cm') {
    return Array.from({ length: count }, (_, i) => {
      const dist = i === 0 ? 30 : 100 * i;
      // No roman parens. Last entry is "Top (<dist>)" where <dist>
      // is the distance value at that position.
      if (i === count - 1 && count > 1) {
        return `Top (${dist})`;
      }
      return String(dist);
    });
  }
  // 'other' — first location is "30 (i)" (same starting offset as the
  // fixed choices); last location is "Top (n)" with roman n; middle
  // locations are plain roman parens.
  return Array.from({ length: count }, (_, i) => {
    const roman = toRomanParens(i + 1);
    if (i === 0)                  return `30 ${roman}`;
    if (i === count - 1)          return `Top ${roman}`;
    return roman;
  });
}

// Returns roman-top labels: (i)(ii)…Top (n). Used by supporting
// columns — each column's locations are (i), (ii), …, Top (n)
// (per v3.1.16 spec).
function makeRomanTopLabels(count) {
  return Array.from({ length: count }, (_, i) => {
    const roman = toRomanParens(i + 1);
    return i === count - 1 ? `Top ${roman}` : roman;
  });
}

function makeRowLabels(cfg, count) {
  switch (cfg.type) {
    case 'fixed-rows': return cfg.rows;
    case 'roman':      return Array.from({ length: count }, (_, i) => toRomanParens(i + 1));
    case 'roman-center': return makeRomanCenterLabels(count);
    case 'nozzle':     return Array.from({ length: count }, (_, i) => `N${i + 1}`);
    case 'numeral':
    default:           return Array.from({ length: count }, (_, i) => String(i + 1));
  }
}

// ── Modal ──────────────────────────────────────────────────────────
export default function UTLayoutConfigModal({
  component,
  measurement,
  scanlineCount,
  positionCount,
  onCancel,
  onConfirm,
}) {
  const cfg = getLayoutConfig(component, measurement);

  const colMode  = (scanlineCount != null && scanlineCount !== '') ? 'scanlines'
    : (positionCount != null && positionCount !== '') ? 'positions'
    : null;
  const colCount  = Number(colMode === 'scanlines' ? scanlineCount : positionCount) || (colMode === 'positions' ? 4 : 8);
  const colPrefix = colMode === 'positions' ? 'POS' : 'SL';

  // ── All hooks before any early return ──
  const [rowCountStr, setRowCountStr] = useState(
    cfg?.defaultColumnCount != null ? String(cfg.defaultColumnCount)
      : cfg?.defaultRowCount != null ? String(cfg.defaultRowCount)
      : '',
  );
  // Supporting-column second count retired in v3.1.15 — per-column
  // counts are collected via the columnLocCounts state below
  // (mirrors pipeLocCounts).
  // Whether to include the "Nominal value (mm)" column. Cells are left blank
  // for the user to fill in via the spreadsheet.
  const [includeNominal, setIncludeNominal] = useState(true);
  // Nozzle: Neck/Plate checkboxes (fixed 2)
  const [neckChecked,  setNeckChecked]  = useState(true);
  const [plateChecked, setPlateChecked] = useState(true);
  // Rafter: per-position checkboxes (variable length)
  const [posChecked, setPosChecked] = useState(
    () => (cfg?.positions ?? []).map(() => true),
  );
  // Scanline-checkboxes (floor external): per-option checkboxes
  const [optChecked, setOptChecked] = useState(
    () => (cfg?.options ?? []).map(() => true),
  );
  // Shell-course: radio for measurements per course ('3' | '4' | '5' | 'variable')
  const [shellCoursePer, setShellCoursePer] = useState('3');
  // Shell-course: per-course measurement counts when 'variable' is selected.
  // Keyed by course index so resizing the course count keeps existing values.
  const [shellVarCounts, setShellVarCounts] = useState({});
  // Pipe-segment: per-pipe location counts.
  const [pipeLocCounts, setPipeLocCounts] = useState({});
  // Supporting-column per-column location counts (same shape as pipes).
  const [columnLocCounts, setColumnLocCounts] = useState({});
  const [error, setError] = useState('');

  if (!cfg) return null;

  const subtitle    = [component, measurement].filter(Boolean).join(' – ');
  const isFixed            = cfg.type === 'fixed-rows';
  const isNozzle           = cfg.type === 'nozzle';
  const isRafter           = cfg.type === 'rafter';
  const isCheckboxes       = cfg.type === 'scanline-checkboxes';
  const isSupportingColumn = cfg.type === 'supporting-column';
  const isShellCourse      = cfg.type === 'shell-course';
  const isPipeSegment      = cfg.type === 'pipe-segment';
  // Types that need a row-count text input
  const needsCount  = !isFixed && !isCheckboxes;
  const previewCols = Array.from({ length: colCount }, (_, i) => `${colPrefix}${i + 1}`);

  // ── Toggle helper for boolean arrays ──
  function toggleAt(arr, setArr, idx) {
    const next = [...arr];
    next[idx] = !next[idx];
    setArr(next);
  }

  // ── Apply ──────────────────────────────────────────────────────
  //
  // Builds the cfg payload consumed by UTTablePage to assemble the
  // FortuneSheet. We additionally attach `recipe` — a flat object of raw
  // inputs (scanlineCount, positionCount, locationCount, plateCount, …,
  // shellCoursePer) — so UTTablePage can ship them straight to the image
  // recipe engine without re-deriving from the sheet shape.
  function buildRecipeInputs(extra = {}) {
    return {
      scanlineCount: colMode === 'scanlines' ? colCount : '',
      positionCount: colMode === 'positions' ? colCount : '',
      ...extra,
    };
  }

  function handleApply() {
    setError('');

    // ── Scanline-checkboxes path (floor external) ──
    // Outside edge of bottom now emits a TRANSPOSED table: selected
    // locations become COLUMN headers across the top, scanlines run
    // down the first column, and MIN/MAX/AVG (+ degradation if
    // nominal) summary rows are auto-added with formulas.
    if (isCheckboxes) {
      const locations = cfg.options.filter((_, i) => optChecked[i]);
      if (locations.length === 0) {
        setError('Select at least one location.');
        return;
      }
      onConfirm({
        specialType: 'outside-edge-bottom',
        locations,
        includeNominal,
        colPrefix,
        colCount,
        recipe: buildRecipeInputs(),
      });
      return;
    }

    // ── Nozzle path ──
    if (isNozzle) {
      const n = parseInt(rowCountStr, 10);
      if (!rowCountStr || !Number.isInteger(n) || n < 1) {
        setError('Please enter a valid whole number (minimum 1).');
        return;
      }
      if (n > 100) { setError('Maximum 100 nozzles.'); return; }
      if (!neckChecked && !plateChecked) {
        setError('Select at least one location (Neck or Plate).');
        return;
      }
      const locations = [
        ...(neckChecked  ? ['Neck (i)']  : []),
        ...(plateChecked ? ['Plate (ii)'] : []),
      ];
      onConfirm({ specialType: 'nozzle-locations', nozzleCount: n, locations, includeNominal, colPrefix, colCount, recipe: buildRecipeInputs({ nozzleCount: n }) });
      return;
    }

    // ── Rafter path ──
    if (isRafter) {
      const n = parseInt(rowCountStr, 10);
      if (!rowCountStr || !Number.isInteger(n) || n < 1) {
        setError('Please enter a valid whole number (minimum 1).');
        return;
      }
      if (n > 100) { setError('Maximum 100 locations.'); return; }
      const positions = cfg.positions.filter((_, i) => posChecked[i]);
      if (positions.length === 0) {
        setError('Select at least one position.');
        return;
      }
      const locationLabels = makeRomanCenterLabels(n);
      onConfirm({ specialType: 'rafter-locations', locationLabels, positions, includeNominal, colPrefix, colCount, recipe: buildRecipeInputs({ locationCount: n }) });
      return;
    }

    // ── Shell-course path ──
    if (isShellCourse) {
      const n = parseInt(rowCountStr, 10);
      if (!rowCountStr || !Number.isInteger(n) || n < 1) {
        setError('Please enter a valid number of shell courses.');
        return;
      }
      if (n > 100) { setError('Maximum 100 shell courses.'); return; }

      let perCourse;
      const isPerCourseInput = shellCoursePer === 'other' || shellCoursePer === '10cm';
      if (isPerCourseInput) {
        perCourse = [];
        for (let i = 0; i < n; i++) {
          const c = parseInt(shellVarCounts[i] ?? '', 10);
          if (!Number.isInteger(c) || c < 1) {
            setError(`Please enter a valid number of measurements for Shell Course ${i + 1}.`);
            return;
          }
          if (c > 100) { setError('Maximum 100 measurements per course.'); return; }
          perCourse.push(c);
        }
      } else {
        const c = parseInt(shellCoursePer, 10);
        perCourse = Array(n).fill(c);
      }

      const courses = perCourse.map((cnt, ci) => ({
        label: `Shell Course ${ci + 1}`,
        locations: makeShellCourseLocations(cnt, shellCoursePer),
      }));
      onConfirm({ specialType: 'shell-course', courses, includeNominal, colPrefix, colCount, recipe: buildRecipeInputs({ courseCount: n, shellCoursePer }) });
      return;
    }

    // ── Pipe-segment path ──
    if (isPipeSegment) {
      const n = parseInt(rowCountStr, 10);
      if (!rowCountStr || !Number.isInteger(n) || n < 1) {
        setError('Please enter a valid number of pipes.');
        return;
      }
      if (n > 100) { setError('Maximum 100 pipes.'); return; }
      const pipes = [];
      for (let i = 0; i < n; i++) {
        const c = parseInt(pipeLocCounts[i] ?? '', 10);
        if (!Number.isInteger(c) || c < 1) {
          setError(`Please enter a valid number of locations for Pipe ${i + 1}.`);
          return;
        }
        if (c > 100) { setError('Maximum 100 locations per pipe.'); return; }
        pipes.push({
          label: `Pipe ${i + 1}`,
          locations: Array.from({ length: c }, (_, j) => String(j + 1)),
        });
      }
      onConfirm({ specialType: 'pipe-segment', pipes, includeNominal, colPrefix, colCount, recipe: buildRecipeInputs({ pipeCount: n }) });
      return;
    }

    // ── Supporting-column path ──
    // Same shape as pipe-segment: user picks # of columns, then enters
    // a location count per column. Labels are plain numerals 1, 2, …
    // with the final one replaced by "Top (N)" (mirrors "Center (n)"
    // from the roman-center labelling). Each column becomes its own
    // split sub-table downstream via buildPipeSegmentTemplate (with
    // groupHeader: 'Column').
    if (isSupportingColumn) {
      const n = parseInt(rowCountStr, 10);
      if (!rowCountStr || !Number.isInteger(n) || n < 1) {
        setError('Please enter a valid number of columns.');
        return;
      }
      if (n > 100) { setError('Maximum 100 columns.'); return; }
      const columns = [];
      for (let i = 0; i < n; i++) {
        const c = parseInt(columnLocCounts[i] ?? '', 10);
        if (!Number.isInteger(c) || c < 1) {
          setError(`Please enter a valid number of locations for Column ${i + 1}.`);
          return;
        }
        if (c > 100) { setError('Maximum 100 locations per column.'); return; }
        // Roman-top labels: (i), (ii), …, Top (iv). Last location is
        // the "Top" of the column with the roman index in parens.
        const locations = makeRomanTopLabels(c);
        columns.push({ label: `Column ${i + 1}`, locations });
      }
      onConfirm({ specialType: 'supporting-column', columns, includeNominal, colPrefix, colCount, recipe: buildRecipeInputs({ columnCount: n }) });
      return;
    }

    // ── Standard path (roman, roman-center, numeral, fixed-rows) ──
    let rowLabels;
    if (isFixed) {
      rowLabels = cfg.rows;
    } else {
      const n = parseInt(rowCountStr, 10);
      if (!rowCountStr || !Number.isInteger(n) || n < 1) {
        setError('Please enter a valid whole number (minimum 1).');
        return;
      }
      if (n > 100) { setError('Maximum 100 rows.'); return; }
      // Corner-weld is special: 1 location → "A-Height" (no parens);
      // 2+ locations → "A-Height (i)", "A-Height (ii)", …
      if (cfg.specialType === 'corner-weld') {
        rowLabels = n === 1
          ? ['A-Height']
          : Array.from({ length: n }, (_, i) => `A-Height ${toRomanParens(i + 1)}`);
      } else {
        rowLabels = makeRowLabels(cfg, n);
      }
    }
    // Map cfg type → the matching layout-count token. Standard-path covers:
    //   roman / roman-center / numeral / fixed-rows.
    // Annular & membrane plates use `plate_count`; everything else uses
    // `location_count` (the user-facing label is always "Number of locations").
    const stdN = isFixed ? '' : parseInt(rowCountStr, 10);
    const extraRecipe = {};
    if (cfg.specialType === 'annular-plates') {
      extraRecipe.plateCount = stdN;
    } else {
      extraRecipe.locationCount = stdN;
    }

    onConfirm({
      // Some standard-path configs (eg annular plates) opt-in to a
      // dedicated builder via cfg.specialType — pass it through.
      specialType: cfg.specialType,
      rowLabels,
      rowHeader: cfg.rowHeader,
      includeNominal,
      colPrefix,
      colCount,
      recipe: buildRecipeInputs(extraRecipe),
    });
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal ut-layout-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Configure Layout</h2>
        {subtitle && <div className="ut-layout-subtitle">{subtitle}</div>}

        {/* Row count input (roman, roman-center, numeral, nozzle, rafter) */}
        {needsCount && (
          <div className="ut-layout-field">
            <label className="ut-layout-label">
              {isSupportingColumn ? cfg.columnCountLabel
                : isShellCourse  ? cfg.courseCountLabel
                : isPipeSegment  ? cfg.pipeCountLabel
                : cfg.rowCountLabel}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="ut-layout-input"
              value={rowCountStr}
              onChange={(e) => setRowCountStr(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Enter a whole number"
              autoFocus
            />
          </div>
        )}

        {/* Supporting-column: per-column location-count textboxes
            (mirrors pipe-segment UX). */}
        {isSupportingColumn && parseInt(rowCountStr, 10) > 0 && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">Locations in each column</div>
            {Array.from({ length: Math.min(parseInt(rowCountStr, 10), 100) }, (_, i) => (
              <div key={i} className="ut-layout-shell-var-row">
                <span className="ut-layout-shell-var-label">Column {i + 1}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ut-layout-input ut-layout-shell-var-input"
                  value={columnLocCounts[i] ?? ''}
                  onChange={(e) => setColumnLocCounts({
                    ...columnLocCounts,
                    [i]: e.target.value.replace(/[^0-9]/g, ''),
                  })}
                  placeholder="Locations"
                />
              </div>
            ))}
          </div>
        )}

        {/* Scanline-checkboxes: fixed options, user ticks which to include */}
        {isCheckboxes && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">{cfg.checkboxLabel}</div>
            <div className="ut-layout-checkboxes ut-layout-checkboxes-col">
              {cfg.options.map((opt, i) => (
                <label key={i} className="ut-layout-checkbox-label">
                  <input
                    type="checkbox"
                    checked={optChecked[i]}
                    onChange={() => toggleAt(optChecked, setOptChecked, i)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Shell-course: radio buttons (3/4/5/Variable) */}
        {isShellCourse && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">Measurements per shell course</div>
            <div className="ut-layout-checkboxes ut-layout-checkboxes-col">
              {[
                { val: '3',     label: '3 measurements' },
                { val: '4',     label: '4 measurements' },
                { val: '5',     label: '5 measurements' },
                { val: 'other', label: 'Other' },
                { val: '10cm',  label: '10cm measurements' },
              ].map((opt) => (
                <label key={opt.val} className="ut-layout-checkbox-label">
                  <input
                    type="radio"
                    name="shellCoursePer"
                    value={opt.val}
                    checked={shellCoursePer === opt.val}
                    onChange={() => setShellCoursePer(opt.val)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Shell-course: per-course textboxes (only when Variable + count entered) */}
        {isShellCourse && (shellCoursePer === 'other' || shellCoursePer === '10cm') && parseInt(rowCountStr, 10) > 0 && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">Measurements in each shell course</div>
            {Array.from({ length: Math.min(parseInt(rowCountStr, 10), 100) }, (_, i) => (
              <div key={i} className="ut-layout-shell-var-row">
                <span className="ut-layout-shell-var-label">Shell Course {i + 1}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ut-layout-input ut-layout-shell-var-input"
                  value={shellVarCounts[i] ?? ''}
                  onChange={(e) => setShellVarCounts({
                    ...shellVarCounts,
                    [i]: e.target.value.replace(/[^0-9]/g, ''),
                  })}
                  placeholder="Measurements"
                />
              </div>
            ))}
          </div>
        )}

        {/* Pipe-segment: per-pipe location-count textboxes */}
        {isPipeSegment && parseInt(rowCountStr, 10) > 0 && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">Locations in each pipe</div>
            {Array.from({ length: Math.min(parseInt(rowCountStr, 10), 100) }, (_, i) => (
              <div key={i} className="ut-layout-shell-var-row">
                <span className="ut-layout-shell-var-label">Pipe {i + 1}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ut-layout-input ut-layout-shell-var-input"
                  value={pipeLocCounts[i] ?? ''}
                  onChange={(e) => setPipeLocCounts({
                    ...pipeLocCounts,
                    [i]: e.target.value.replace(/[^0-9]/g, ''),
                  })}
                  placeholder="Locations"
                />
              </div>
            ))}
          </div>
        )}

        {/* Nozzle: Neck / Plate location checkboxes */}
        {isNozzle && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">Locations per nozzle</div>
            <div className="ut-layout-checkboxes ut-layout-checkboxes-col">
              <label className="ut-layout-checkbox-label">
                <input type="checkbox" checked={neckChecked}  onChange={(e) => setNeckChecked(e.target.checked)}  />
                Neck (i)
              </label>
              <label className="ut-layout-checkbox-label">
                <input type="checkbox" checked={plateChecked} onChange={(e) => setPlateChecked(e.target.checked)} />
                Plate (ii)
              </label>
            </div>
          </div>
        )}

        {/* Rafter: per-type position checkboxes */}
        {isRafter && (
          <div className="ut-layout-field">
            <div className="ut-layout-label">Positions per location</div>
            <div className="ut-layout-checkboxes ut-layout-checkboxes-col">
              {cfg.positions.map((pos, i) => (
                <label key={i} className="ut-layout-checkbox-label">
                  <input
                    type="checkbox"
                    checked={posChecked[i]}
                    onChange={() => toggleAt(posChecked, setPosChecked, i)}
                  />
                  {pos}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Nominal value column toggle — values entered in the spreadsheet */}
        <div className="ut-layout-field">
          <label className="ut-layout-checkbox-label">
            <input
              type="checkbox"
              checked={includeNominal}
              onChange={(e) => setIncludeNominal(e.target.checked)}
            />
            Include Nominal value (mm) column
          </label>
        </div>

        {/* Column summary */}
        <div className="ut-layout-col-info">
          <span className="ut-layout-col-info-label">
            {colMode === 'scanlines' ? 'Scanlines' : colMode === 'positions' ? 'Positions' : 'Columns'}:
          </span>
          <span className="ut-layout-col-info-cols">{previewCols.join(', ')}</span>
          <span className="ut-layout-col-info-hint">(change count in Edit table modal)</span>
        </div>

        {error && <div className="ut-layout-error">{error}</div>}

        <div className="modal-actions ut-layout-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </div>
  );
}
